import React, { useMemo, useState } from 'react';
import { ArrowUpDownIcon, ExternalLinkIcon, SearchIcon } from 'lucide-react';
import { Loan } from '../types/commission';
import { commissionFor, formatCurrency, formatDate } from '../utils/commission';
interface LoanBreakdownTableProps {
  /** Funded loans only — pipeline loans never appear here. */
  loans: Loan[];
  /** BPS applied to every funded loan in the period. */
  earnedBps: number;
  periodLabel: string;
  /** Hides interactive controls while the page is captured for PDF export. */
  hideSearch?: boolean;
}
type SortKey = 'fundedDate' | 'amount' | 'commission';
export function LoanBreakdownTable({
  loans,
  earnedBps,
  periodLabel,
  hideSearch = false
}: LoanBreakdownTableProps) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('fundedDate');
  const [descending, setDescending] = useState(false);
  const rows = useMemo(() => {
    const enriched = loans.map(loan => ({
      loan,
      commission: commissionFor(loan.amount, earnedBps)
    }));
    const needle = query.trim().toLowerCase();
    const filtered = enriched.filter(({
      loan
    }) => {
      if (needle.length === 0) return true;
      return loan.borrower.toLowerCase().includes(needle) || loan.id.toLowerCase().includes(needle) || loan.product.toLowerCase().includes(needle);
    });
    return filtered.sort((a, b) => {
      const direction = descending ? -1 : 1;
      if (sortKey === 'amount') return (a.loan.amount - b.loan.amount) * direction;
      if (sortKey === 'commission') return (a.commission - b.commission) * direction;
      return a.loan.fundedDate.localeCompare(b.loan.fundedDate) * direction;
    });
  }, [loans, earnedBps, query, sortKey, descending]);
  const totalCommission = rows.reduce((sum, row) => sum + row.commission, 0);
  const totalVolume = rows.reduce((sum, row) => sum + row.loan.amount, 0);
  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setDescending(value => !value);
      return;
    }
    setSortKey(key);
    setDescending(key !== 'fundedDate');
  }
  return <section aria-labelledby="loans-heading" className="rounded-xl border border-slate-200 bg-white shadow-card" data-id="element-19" data-mp="2qbjkor5zv2gx">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-20" data-mp="2ud530s1as8yi">
        <div data-id="element-21" data-mp="2zb6h0yjv9i34">
          <h2 id="loans-heading" className="text-base font-bold text-slate-900" data-id="element-22" data-mp="2zbonsp45nepa">
            Funded loans · {periodLabel}
          </h2>
          
        </div>

        <div className={`relative ${hideSearch ? 'hidden' : ''}`} data-id="element-23" data-mp="26n11cq7o6ja5">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-24" data-mp="2bmvqao7vwysk" />
          <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search borrower or loan #" aria-label="Search funded loans" className="w-72 rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400" data-id="element-25" data-mp="2geng2wj7v0hn" />
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-26" data-mp="1gcgxk5u8bpog">
        <table className="w-full min-w-[640px] text-left text-sm" data-id="element-27" data-mp="1bep9xo0mapq2">
          <caption className="sr-only" data-id="element-28" data-mp="1bf8k2ik109tj">Breakdown of funded loans and commission earned in {periodLabel}</caption>
          <thead data-id="element-29" data-mp="16hp3vd3rpmyy">
            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-30" data-mp="12e0wauf4yrdp">
              <th scope="col" className="px-6 py-3" data-id="element-31" data-mp="xf7eipstge7q">
                Borrower
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-32" data-mp="snxrlwynjozj">
                Loan #
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-33" data-mp="nol3l8w1obvd">
                Product
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-34" data-mp="iqjn8vq06qnl">
                <SortButton label="Funded" active={sortKey === 'fundedDate'} onClick={() => toggleSort('fundedDate')} data-id="element-35" data-mp="inyj2028fymo" />
              </th>
              <th scope="col" className="px-6 py-3 text-right" data-id="element-36" data-mp="9plek04mduyo">
                <SortButton label="Loan amount" active={sortKey === 'amount'} onClick={() => toggleSort('amount')} align="right" data-id="element-37" data-mp="9rg3dpj0i781" />
              </th>
              <th scope="col" className="px-6 py-3 text-right" data-id="element-38" data-mp="2dtg2zdjqmjtx">
                <SortButton label="Commission" active={sortKey === 'commission'} onClick={() => toggleSort('commission')} align="right" data-id="element-39" data-mp="201t4d72boz7z" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-40" data-mp="24yjj9r2e5ort">
            {rows.length === 0 ? <tr data-id="element-41" data-mp="1q2alyv229li1">
                <td colSpan={6} className="px-6 py-14 text-center" data-id="element-42" data-mp="1q5ygnxj8sdyc">
                  <p className="text-sm font-semibold text-slate-700" data-id="element-43" data-mp="1v49qi89jeogk">
                    {loans.length === 0 ? 'No funded loans in this period' : 'No loans match your search'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-44" data-mp="1m2jzh3jkhrjb">
                    {loans.length === 0 ? 'Loans appear here as soon as they fund.' : 'Try a different borrower name or loan number.'}
                  </p>
                </td>
              </tr> : rows.map(({
            loan,
            commission
          }) => <tr key={loan.id} className="transition hover:bg-slate-50" data-id="element-45" data-mp="3qk81l06yibx5">
                  <th scope="row" className="px-6 py-4 font-semibold" data-id="element-46" data-mp="3lm6hqd1pi8km">
                    <a href={`#/loans/${loan.id}`} onClick={event => event.preventDefault()} aria-label={`Open loan page for ${loan.borrower}`} className="group inline-flex items-center gap-1.5 text-brand-700 underline decoration-brand-200 decoration-2 underline-offset-2 transition hover:text-brand-900 hover:decoration-brand-500" data-id="element-47" data-mp="37fen0tifdn7">
                      {loan.borrower}
                      <ExternalLinkIcon className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" aria-hidden="true" data-id="element-48" data-mp="m5rroxnjl7o2" />
                    </a>
                  </th>
                  <td className="px-6 py-4 tabular-nums text-slate-500" data-id="element-49" data-mp="i4y1fxmmngs0">{loan.id}</td>
                  <td className="px-6 py-4 text-slate-700" data-id="element-50" data-mp="2nxmz312rmq0h">{loan.product}</td>
                  <td className="px-6 py-4 tabular-nums text-slate-700" data-id="element-51" data-mp="2jwl08lcrgfz1">{formatDate(loan.fundedDate)}</td>
                  <td className="px-6 py-4 text-right tabular-nums text-slate-900" data-id="element-52" data-mp="mw70jigqwdbx">{formatCurrency(loan.amount)}</td>
                  <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-900" data-id="element-53" data-mp="rui79kbqu7px">
                    {formatCurrency(commission)}
                  </td>
                </tr>)}
          </tbody>
          {rows.length > 0 ? <tfoot data-id="element-54" data-mp="11q38hg8zn3ym">
              <tr className="border-t border-slate-200 bg-slate-50 text-sm" data-id="element-55" data-mp="15u159kauc2oi">
                <th scope="row" colSpan={4} className="px-6 py-4 text-left font-bold text-slate-900" data-id="element-56" data-mp="15rx43a4bthrx">
                  {rows.length} funded {rows.length === 1 ? 'loan' : 'loans'}
                </th>
                <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-900" data-id="element-57" data-mp="1flx5b0whm2vd">
                  {formatCurrency(totalVolume)}
                </td>
                <td className="px-6 py-4 text-right font-bold tabular-nums text-brand-800" data-id="element-58" data-mp="1pj1rv1u8w0cp">
                  {formatCurrency(totalCommission)}
                </td>
              </tr>
            </tfoot> : null}
        </table>
      </div>
    </section>;
}
interface SortButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  align?: 'left' | 'right';
}
function SortButton({
  label,
  active,
  onClick,
  align = 'left'
}: SortButtonProps) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition hover:text-brand-700 ${active ? 'text-brand-700' : 'text-slate-500'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-59" data-mp="301vassj9c4pt">
      {label}
      <ArrowUpDownIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-60" data-mp="3nzkg5pmxjy0n" />
    </button>;
}